import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type SpinningLogoSize = 'sm' | 'md' | 'lg' | 'xl';
export type SpinningLogoSpeed = 'slow' | 'normal' | 'fast';

export interface SpinningLogoProps {
  size?: SpinningLogoSize;
  speed?: SpinningLogoSpeed;
  className?: string;
  /** accessible label — read by screen readers, otherwise treated as decorative */
  label?: string;
}

const SIZE_PX: Record<SpinningLogoSize, number> = {
  sm: 28,
  md: 48,
  lg: 72,
  xl: 112,
};

const SPEED_S: Record<SpinningLogoSpeed, number> = {
  slow: 4,
  normal: 2.4,
  fast: 1.2,
};

/**
 * Wassel loading spinner — a calm green arc on a mint track.
 * Reskinned for the v1 brand identity: NO radar ring / gradient / amber dot —
 * the radar-circle is explicitly rejected as a brand mark. This is a purely
 * functional progress indicator (green on mint), not a logo.
 */
export default function SpinningLogo({
  size = 'lg',
  speed = 'normal',
  className,
  label,
}: SpinningLogoProps) {
  const px = SIZE_PX[size];
  const duration = SPEED_S[speed];

  return (
    <span
      className={cn('inline-flex items-center justify-center', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ width: px, height: px }}
    >
      <motion.svg
        width={px}
        height={px}
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        animate={{ rotate: 360 }}
        transition={{ duration, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: 'center', display: 'block' }}
      >
        {/* Track — soft mint ring */}
        <circle cx="40" cy="40" r="32" stroke="#E7F1EC" strokeWidth="6" />
        {/* Arc — Wassel green, ~quarter of the ring */}
        <circle
          cx="40"
          cy="40"
          r="32"
          stroke="#15806A"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray="50 200"
        />
      </motion.svg>
    </span>
  );
}
